import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core'
import { CookieService } from 'ngx-cookie-service'
import { CookieKeys } from '../../enums/cookie-keys'
import { CmsService, TranslationService, WindowRef } from '@spartacus/core'
import { EMPTY, Observable } from 'rxjs'
import { CmsCountryService } from '../../services/cms-country/cms-country.service'
import { map, switchMap } from 'rxjs/operators'
import { CountryBarBtn, CountryBarTranslation } from './country-bar.module'
import { MbConstants } from '../../../shared/mb-constants'
import { CountryLanguageService } from '../../../site-context/facade/country-language.service'
import { CountryService } from '../../../site-context/facade/country.service'
import { CountryDTO } from '../types'

@Component({
  selector: 'mb-country-bar',
  templateUrl: './country-bar.component.html',
  styleUrls: ['./country-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CountryBarComponent implements OnInit {

  @Output() closeEvent = new EventEmitter()

  title$: Observable<string> = EMPTY
  leftBtn$: Observable<CountryBarBtn> = EMPTY
  rightBtn$: Observable<CountryBarBtn> = EMPTY

  constructor (private cookieService: CookieService,
               private translation: TranslationService,
               private cmsCountryService: CmsCountryService,
               protected winRef: WindowRef,
               private cmsService: CmsService,
               private countryLanguageService: CountryLanguageService,
               private countryService: CountryService) {
  }

  ngOnInit (): void {
    const fallbackCountry = this.cmsCountryService.getCountryByISO(MbConstants.FALLBACK_COUNTRY)
    const miltenyiCountry = this.cmsCountryService.getCountryByISO(this.cookieService.get(CookieKeys.MILTENYI_COUNTRY))
    const miltenyiSelectedCountry = this.cmsCountryService.getCountryByISO(this.cookieService.get(CookieKeys.MILTENYI_SELECT_COUNTRY))
    const navigationCountry = this.cmsCountryService.getCountryByISO(this.cookieService.get(CookieKeys.NAVIGATION_COUNTRY)) ??
      fallbackCountry
    if (miltenyiCountry) {
      // If selected country present -> show "You previously chosen..." dialog
      this.title$ = this.translation.translate('countryBar.previouslyChosen', { country: miltenyiCountry.name })
      if (miltenyiSelectedCountry?.ISO === miltenyiCountry?.ISO) {
        // If selected country === MILTENYI_SELECT_COUNTRY -> stay MILTENYI_SELECT_COUNTRY, goTo NAVIGATION_COUNTRY
        this.leftBtn$ = this.getStayBtn(miltenyiCountry)
        this.rightBtn$ = this.getSwitchBtn({ key: 'countryBar.goToAndUpdate', country: navigationCountry?.adjective ?? '' },
          navigationCountry?.ISO, miltenyiCountry?.ISO, navigationCountry?.ISO)
      } else {
        // If selected country !== MILTENYI_SELECT_COUNTRY -> stay SELECTED_COUNTRY, goTo NAVIGATION_COUNTRY if !== SELECTED_COUNTRY,
        // otherwise goTo MILTENYI_SELECT_COUNTRY
        const goToCountry = miltenyiSelectedCountry?.ISO === navigationCountry?.ISO
          ? miltenyiSelectedCountry
          : navigationCountry?.ISO === miltenyiCountry?.ISO ? miltenyiSelectedCountry : navigationCountry
        this.leftBtn$ = this.getStayBtn(miltenyiCountry)
        this.rightBtn$ = this.getSwitchBtn({ key: 'countryBar.goToAndUpdate', country: goToCountry?.adjective ?? '' },
          goToCountry?.ISO, navigationCountry?.ISO, goToCountry?.ISO)
      }
    } else {
      // If SELECTED_COUNTRY !== MILTENYI_SELECT_COUNTRY -> show "You are coming from..." dialog
      // Stay NAVIGATION_COUNTRY, goTo MILTENYI_SELECT_COUNTRY
      this.title$ = this.translation.translate('countryBar.comingFrom', { country: miltenyiSelectedCountry?.name ?? '' })
      this.leftBtn$ = this.getSwitchBtn({ key: 'countryBar.goTo', country: miltenyiSelectedCountry?.adjective ?? '' },
        miltenyiSelectedCountry?.ISO, navigationCountry?.ISO, miltenyiSelectedCountry?.ISO)
      this.rightBtn$ = this.getStayBtn()
    }
    if (!this.winRef.document?.body.classList.contains('country-notification-bottom')) {
      this.winRef.document.body.classList.add('country-notification-bottom')
    }
    this.cookieService.delete(CookieKeys.NAVIGATION_COUNTRY, '/')
  }

  close (navigate: boolean, country: string = MbConstants.FALLBACK_COUNTRY): void {
    this.cookieService.set(CookieKeys.MILTENYI_COUNTRY, country, { path: '/' })
    this.cookieService.set(CookieKeys.MILTENYI_SELECT_COUNTRY, country, { path: '/' })
    this.cookieService.delete(CookieKeys.NAVIGATION_COUNTRY, '/')
    this.closeEvent.emit()
    if (navigate) {
      this.changeActiveCountry(`${country}-en`)
    }
  }

  private changeActiveCountry (countryLanguage: string): void {
    this.countryLanguageService.setActive(countryLanguage)
    this.cmsService.refreshLatestPage()
    if(this.winRef.isBrowser()) {
      this.winRef.nativeWindow?.location.reload()
    }
  }

  private getStayBtn (country?: CountryDTO): Observable<CountryBarBtn> {
    return this.countryService.getActive().pipe(
      map(activeCountry => this.cmsCountryService.getCountryByISO(activeCountry)),
      switchMap(activeCountry => this.translation.translate('countryBar.stay', {
        countryAdjective: country?.adjective || activeCountry?.adjective || ''
      }).pipe(
        map(text => ({
          text,
          alt: `current country ${country?.ISO || activeCountry?.ISO}`,
          title: `current country ${country?.ISO || activeCountry?.ISO}`,
          eventAction: `Entered on ${country?.ISO || activeCountry?.ISO}`,
          eventLabel: `Stay at ${country?.ISO || activeCountry?.ISO}`,
          country: country?.ISO || activeCountry?.ISO || '',
          class: 'stay-at',
          navigate: false
        }))
      ))
    )
  }

  private getSwitchBtn (translation: CountryBarTranslation, alternativeCountry = '', enteredOn = '',
                        country = ''): Observable<CountryBarBtn> {
    return this.translation.translate(translation.key, { countryAdjective: translation.country }).pipe(
      map(text => ({
        text,
        alt: `alternative country ${alternativeCountry}`,
        title: `alternative country ${alternativeCountry}`,
        eventAction: `Entered on ${enteredOn}`,
        eventLabel: `Switch to ${alternativeCountry}`,
        country: country,
        class: 'switch-to',
        navigate: true
      }))
    )
  }
}
